"use client";

import { motion } from "framer-motion";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import Banner from "../Banner";
import { config } from "@/lib/config";
import { resolveLink } from "@/utils";
import dynamic from "next/dynamic";
import type SliderType from "react-slick";

// Lazy load slider with loading component
const Slider = dynamic(() => import("react-slick"), {
  ssr: false,
  loading: () => (
    <div className="w-full h-[720px] mobileMax:h-[500px] bg-white animate-pulse" />
  ),
}) as unknown as React.FC<SliderType["props"]>;

interface BannerProps {
  mediaItems?: any;
}

const HomeSlider: React.FC<BannerProps> = ({ mediaItems }) => {
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const sliderRef = useRef<SliderType | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isSliderReady, setIsSliderReady] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      speed: 800,
      infinite: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 6000,
      pauseOnHover: false,
      pauseOnFocus: false,
      pauseOnDotsHover: false,
      arrows: false,
      fade: false,
      lazyLoad: "ondemand" as const, // Lazy load slides
      cssEase: "cubic-bezier(0.25, 0.1, 0.25, 1.0)",
      beforeChange: (_current: number, next: number) => {
        setActiveSlide(next);
        const isVideo = !!mediaItems?.[next]?.field_card_video_file;

        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        if (isVideo) {
          sliderRef.current?.slickPause();
          timeoutRef.current = setTimeout(() => {
            sliderRef.current?.slickPlay();
          }, 30000);

          // Optimized video handling
          requestAnimationFrame(() => {
            const video = videoRefs.current[next];
            if (video) {
              video.currentTime = 0;
              video.play().catch(() => {
                /* Ignore autoplay errors */
              });
            }
          });
        } else {
          sliderRef.current?.slickPlay();
        }

        // Pause other videos efficiently
        videoRefs.current.forEach((video, index) => {
          if (video && index !== next && !video.paused) {
            video.pause();
          }
        });
      },
      responsive: [
        {
          breakpoint: 1024,
          settings: { slidesToShow: 1, slidesToScroll: 1, arrows: false },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            dots: true,
          },
        },
      ],
      customPaging: (_i: number) => <span className="custom-dot" />,
    }),
    [mediaItems],
  );

  useEffect(() => {
    if (!mediaItems?.[0]?.field_card_video_file) return;

    const timer = setTimeout(() => {
      const firstVideo = videoRefs.current[0];
      if (sliderRef.current && firstVideo) {
        sliderRef.current.slickPause();
        firstVideo.currentTime = 0;
        firstVideo.play().catch(() => {
          /* Ignore autoplay errors */
        });

        timeoutRef.current = setTimeout(() => {
          sliderRef.current?.slickPlay();
        }, 30000);
      }
      setIsSliderReady(true);
    }, 100);

    return () => {
      clearTimeout(timer);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [mediaItems]);

  const handleSlideChange = useCallback((index: number) => {
    sliderRef.current?.slickGoTo(index);
  }, []);

  // Memoized background gradient
  const backgroundStyle = useMemo(
    () => ({
      background:
        mediaItems && mediaItems.length > 0
          ? "white"
          : "radial-gradient(circle at 41% 94%, #48DBB2, #003350 40%)",
    }),
    [mediaItems?.length],
  );

  // Don't render until we have media items
  if (!mediaItems || mediaItems.length === 0) {
    return (
      <div
        className="banner-wrap-styling h-[720px] mobileMax:h-[500px] relative overflow-hidden w-full box-border"
        style={backgroundStyle}
      />
    );
  }

  return (
    <div
      className="banner-wrap-styling h-[720px] mobileMax:h-[500px] relative overflow-hidden w-full box-border"
      style={backgroundStyle}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="absolute inset-0 w-full h-full z-[1]"
      >
        {/* Custom navigation dots */}
        <div
          className="absolute bottom-12 mobileMax:bottom-5 left-1/2 transform -translate-x-1/2 flex space-x-4 z-50"
          role="tablist"
          aria-label="Slide navigation"
        >
          {mediaItems.map((_: any, index: number) => {
            const isActive = activeSlide === index;

            return (
              <button
                key={index}
                role="tab"
                aria-label={`Go to slide ${index + 1}`}
                aria-selected={isActive}
                aria-current={isActive ? "true" : undefined}
                tabIndex={isActive ? 0 : -1}
                onClick={() => handleSlideChange(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSlideChange(index);
                  }
                }}
                className="focus:outline-none focus-visible:ring-2 ring-offset-2 ring-[#2679A3] rounded-full p-2"
              >
                {isActive ? (
                  <div className="h-4 w-8 rounded-full border bg-gradient-to-l from-[#2679A3] to-[#FFFFFF]" />
                ) : (
                  <div className="w-4 h-4 rounded-full border border-black bg-white" />
                )}
              </button>
            );
          })}
        </div>

        <Slider
          ref={sliderRef}
          {...sliderSettings}
          className="slider-theme-arrow w-full box-border"
        >
          {mediaItems.map((media: any, index: number) => {
            const imageUrl = media?.field_image?.uri?.url
              ? `${config.apiBase}${media.field_image.uri.url}`
              : null;

            return (
              <div
                key={index}
                className="slide-item relative w-full h-full box-border"
              >
                <div className="box-border media-container absolute inset-0 w-full h-[720px] mobileMax:h-[500px] bg-white rounded-[40px] mobileMax:rounded-[10px] overflow-hidden">
                  {imageUrl && (
                    <div className="w-full h-full background-banners bg-white px-[40px] m-0 pt-[40px] mobileToDesk:px-[20px] mobileToDesk:pt-[20px] rounded-[40px] mobileMax:rounded-[10px] overflow-hidden">
                      <Banner
                        title={media?.field_title}
                        subTitle={media?.field_sub_title}
                        description={media?.field_description?.processed}
                        isRounded
                        backgroundImg={imageUrl}
                        buttonText={media?.field_button?.[0]?.title}
                        buttonLink={resolveLink(media?.field_button?.[0]?.uri)}
                        button2Text={media?.field_button?.[1]?.title}
                        button2Link={resolveLink(media?.field_button?.[1]?.uri)}
                        isHomePageSlider
                        className="home-page-banner"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </Slider>
      </motion.div>

      <style jsx global>{`
        .banner-wrap-styling .slick-slider,
        .banner-wrap-styling .slick-list,
        .banner-wrap-styling .slick-track {
          height: 100%;
          width: 100%;
          box-sizing: border-box;
        }
        .banner-wrap-styling .slide-item,
        .banner-wrap-styling .background-banners {
          height: 720px !important;
          border-radius: 40px;
          overflow: hidden;
        }

        /* Optimize rendering with GPU acceleration */
        .banner-wrap-styling .slick-slide {
          will-change: transform;
          transform: translateZ(0);
        }

        .banner-wrap-styling .slick-dots {
          bottom: 80px;
          z-index: 50;
          display: flex !important;
          justify-content: center;
          align-items: center;
          width: 100%;
        }

        .banner-wrap-styling .slick-dots li {
          margin: 0 8px;
          display: inline-block;
        }

        .banner-wrap-styling .slick-dots li button {
          padding: 0;
          border: none;
          background: transparent;
          cursor: pointer;
          display: inline-block;
          width: 12px;
          height: 12px;
          line-height: 0;
          overflow: visible;
        }

        .banner-wrap-styling .slick-dots li button .custom-dot {
          display: block;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.5);
          transition: all 0.25s ease;
        }

        .banner-wrap-styling .slick-dots li.slick-active button .custom-dot {
          background: #ffffff;
          transform: scale(1.25);
        }

        .banner-wrap-styling .slick-dots li button:before {
          display: none;
        }

        @media (max-width: 767px) {
          .banner-wrap-styling .slide-item,
          .banner-wrap-styling .background-banners {
            height: 500px !important;
            border-radius: 10px;
          }
          .banner-wrap-styling .slick-dots {
            bottom: 60px;
          }
        }
      `}</style>
    </div>
  );
};

export default HomeSlider;
